import { useSpring } from '@react-spring/three';
import { OrbitControls } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import React, { useImperativeHandle } from 'react';
import { useView3DCameraRef } from '../../../context/context3D';
import { Box3, Euler, Sphere, Vector2, Vector3 } from 'three';
import { ContentModel } from '@gkn-ili/views/dist/PageContent';

function createAnimateToHomePosition(view: ContentModel['content']['view']) {
  return {
    position: new Vector3(view?.position[0], view?.position[1], view?.position[2]),
    rotation: new Euler(view?.rotation[0], view?.rotation[1], view?.rotation[2]),
    target: new Vector3(view?.target?.[0], view?.target?.[1], view?.target?.[2]),
  };
}
type CameraState = {
  position: Vector3;
  rotation: Euler;
  target: Vector3;
};

export default function App3DCamera(props: { view: ContentModel['content']['view'] }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const orbitControlRef = React.useRef<any>(null);
  const { camera, scene } = useThree();
  const [orbit, setOrbit] = React.useState(true);
  const [animatePosition, setAnimatePosition] = React.useState<{ goto: CameraState; home: CameraState }>({
    goto: createAnimateToHomePosition(props.view),
    home: createAnimateToHomePosition(props.view),
  });

  React.useEffect(() => {
    setAnimatePosition({
      goto: createAnimateToHomePosition(props.view),
      home: createAnimateToHomePosition(props.view),
    });
  }, [props.view]);

  const cameraRef = useView3DCameraRef();

  React.useEffect(() => {
    camera.far = 100000;
    camera.updateProjectionMatrix();
  }, []);

  const [spring] = useSpring(() => {
    const goto = animatePosition.goto;
    const cameraTarget = orbitControlRef.current ? orbitControlRef.current.target : new Vector3();

    return {
      to: {
        posX: goto.position.x,
        posY: goto.position.y,
        posZ: goto.position.z,
        rotX: goto.rotation.x,
        rotY: goto.rotation.y,
        rotZ: goto.rotation.z,
        tarX: goto.target.x,
        tarY: goto.target.y,
        tarZ: goto.target.z,
      },
      from: {
        posX: camera.position.x,
        posY: camera.position.y,
        posZ: camera.position.z,
        rotX: camera.rotation.x,
        rotY: camera.rotation.y,
        rotZ: camera.rotation.z,
        tarX: cameraTarget.x,
        tarY: cameraTarget.y,
        tarZ: cameraTarget.z,
      },
      reset: true,
      onStart: () => {
        setOrbit(false);
      },
      onRest: () => {
        setOrbit(true);
      },
      config: {
        duration: 500,
      },
    };
  }, [animatePosition.goto]);

  useFrame(() => {
    if (orbit) {
      return;
    }
    camera.position.set(spring.posX.get(), spring.posY.get(), spring.posZ.get());
    camera.rotation.set(spring.rotX.get(), spring.rotY.get(), spring.rotZ.get());
    if (orbitControlRef.current) {
      // set target
      orbitControlRef.current.target.set(spring.tarX.get(), spring.tarY.get(), spring.tarZ.get());
      orbitControlRef.current.update();
    }
  });
  useImperativeHandle(
    cameraRef,
    () => ({
      getState() {
        const cameraTarget = orbitControlRef.current ? orbitControlRef.current.target : new Vector3();
        return { position: camera.position, rotation: camera.rotation, target: cameraTarget };
      },
      goHome() {
        setAnimatePosition((current) => ({ goto: { ...current.home }, home: current.home }));
      },
      zoomFit() {
        const box = new Box3();
        box.setFromObject(scene);
        const sphere = new Sphere();
        box.getBoundingSphere(sphere);

        if (orbitControlRef.current) {
          const lookVector = new Vector3();
          lookVector.copy(orbitControlRef.current.target as Vector3);
          lookVector.sub(camera.position).normalize();
          // If for some reason the look vector has length 0, we set it to some standard
          // look vector
          if (lookVector.lengthSq() === 0) {
            lookVector.set(1, 0, 0);
          }

          const distance = sphere.radius / Math.tan((Math.PI * 30) / 180);

          const position = new Vector3();
          position.copy(sphere.center);
          position.sub(lookVector.multiplyScalar(distance));

          const target = new Vector3();
          target.copy(sphere.center);
          const rotation = new Euler();
          rotation.copy(camera.rotation);
          camera.rotation.copy(rotation);

          setAnimatePosition((current) => ({
            goto: {
              position,
              rotation,
              target,
            },
            home: current.home,
          }));
        }
      },
      get2DCoordinates(position) {
        const result = position.project(camera);
        return new Vector2(result.x, result.y);
      },
    }),
    [camera, scene],
  );

  return <OrbitControls enabled={orbit} ref={orbitControlRef} />;
}
